// ============================================================================
// Knopf-Regel des Vario-Slots (listing-status.ts, Schritt „Neu listen oder Aufschalten").
// Bewusst DB- und DOM-frei, damit die Regel testbar ist -- gleiches Muster wie
// listing-mode-choice.ts und publish-button.ts.
//
// Regel des Betreibers (2026-09-13): Aufschalten braucht eine EAN. Neu listen braucht
// EAN UND Hersteller, denn Kaufland verlangt `manufacturer` als Pflichtattribut der
// Produktdaten; beim Aufschalten gehen keine Produktdaten raus, dort ist der Hersteller
// egal. Bis hierher war „Neu listen" nie gesperrt, ein fehlender Hersteller zeigte nur
// einen Hinweis.
//
// „Hersteller vorhanden" heisst: am Vario-Artikel ist ein Hersteller-KONTO verknuepft
// (`manufacturerRef.id`, geliefert als `hasManufacturer`). Nicht das Label: Vario liefert
// `manufacturerRef` zeitweise ohne Label, und die frueher mitgezaehlte Hersteller-
// Artikelnummer ist gar kein Hersteller.
// ============================================================================

export interface SlotModeInput {
  ean: string | null | undefined;
  /** Aus `vario-listing-category`: Hersteller-Konto am Artikel verknuepft. `undefined` bei
   *  einem Backend, das das Feld noch nicht liefert -- dann zaehlt das Label. */
  hasManufacturer: boolean | null | undefined;
  /** Anzeigename des Herstellers; nur Rueckfall fuer die Pruefung, wenn `hasManufacturer` fehlt. */
  manufacturerLabel: string | null | undefined;
  /** Variantenvater: traegt in Vario keine EAN, die liegt an den Varianten. */
  isVariantParent: boolean;
}

export interface SlotKnopf {
  enabled: boolean;
  /** Tooltip: bei gesperrtem Knopf der Grund, sonst die Aktion. */
  title: string;
}

export interface SlotModeButtons {
  /** „Artikel suchen" auf der Karte „Auf bestehendes Angebot aufschalten". */
  attach: SlotKnopf;
  /** „Neu listen". */
  newListing: SlotKnopf;
  /** Fuer die Hinweise unter der Zusammenfassung. Beim Variantenvater ist die EAN nie „fehlend". */
  eanFehlt: boolean;
  herstellerFehlt: boolean;
}

function nichtLeer(s: string | null | undefined): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

export function bestimmeSlotKnoepfe(i: SlotModeInput): SlotModeButtons {
  const eanDa = nichtLeer(i.ean);
  const herstellerDa = typeof i.hasManufacturer === 'boolean' ? i.hasManufacturer : nichtLeer(i.manufacturerLabel);
  const eanFehlt = !eanDa && !i.isVariantParent;
  const herstellerFehlt = !herstellerDa;

  let attach: SlotKnopf;
  if (i.isVariantParent) {
    attach = { enabled: false, title: 'Ein Variantenvater wird nicht selbst aufgeschaltet. Die Varianten im Reiter „Varianten" aufschalten.' };
  } else if (!eanDa) {
    attach = { enabled: false, title: 'Aufschalten braucht eine EAN am Vario-Artikel oder am Verkaufskanal-Listing.' };
  } else {
    attach = { enabled: true, title: 'Artikel per EAN im Kaufland-Katalog suchen' };
  }

  const gruende: string[] = [];
  if (eanFehlt) gruende.push('EAN fehlt');
  if (herstellerFehlt) gruende.push('Hersteller fehlt');
  const newListing: SlotKnopf = gruende.length === 0
    ? { enabled: true, title: 'Artikel bei Kaufland neu anlegen' }
    : {
      enabled: false,
      title: `Neu listen braucht EAN und Hersteller: ${gruende.join(', ')}. Bitte in den Vario-Artikeldetails pflegen.`,
    };

  return { attach, newListing, eanFehlt, herstellerFehlt };
}
